/**
 * Insurance checkout screen layout
 *
 * Shared presentation for the three insurance checkout screens. Consumes the result of
 * `useInsuranceCheckoutScreen` and renders the hero, checkout table, disclaimer, consent
 * checkboxes, and footer. The only per-variant piece is the billing-detail drawer, which each
 * screen passes in via `billingDetailDrawer`.
 */

import { type MouseEvent, type ReactNode } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../styles/colors';
import { fontFamily, space, text } from '../../../../styles/onboardingUiTokens';
import type { UseInsuranceCheckoutScreenResult } from '../../hooks/useInsuranceCheckoutScreen';
import { formatEuro, formatRefundPercent } from '../../insurance/copyUtils';
import { LoadingSpinner } from '../elements/LoadingSpinner';
import { Notice } from '../elements/Notice';
import { Checkbox } from '../forms/Checkbox';
import { SvgIcon } from '../icons/SvgIcon';
import { OnboardingBody } from '../layout/OnboardingBody';
import { OnboardingFooter } from '../layout/OnboardingFooter';
import { OnboardingHeader } from '../layout/OnboardingHeader';
import { OnboardingLayout } from '../layout/OnboardingLayout';
import { Body } from '../typography/Body';
import { OnboardingHeroBanner } from './OnboardingHeroBanner';
import { LiabilityLegalDrawer } from './LiabilityLegalDrawer';
import { OnboardingCheckoutTable } from './OnboardingCheckoutTable';

const linkStyle = { color: colors.primary, textDecoration: 'underline' as const };

function ConsentLink({ href, children, onClick }: { href: string; children: ReactNode; onClick?: () => void }) {
  const trimmed = href.trim();
  if (onClick) {
    return (
      <a
        href="#"
        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          onClick();
        }}
        style={linkStyle}
      >
        {children}
      </a>
    );
  }
  if (!trimmed) {
    return <span style={linkStyle}>{children}</span>;
  }
  return (
    <a href={trimmed} target="_blank" rel="noreferrer" style={linkStyle}>
      {children}
    </a>
  );
}

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`;

const DynamicLoadingArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  width: 100%;
`;

const ConsentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
`;

const DisclaimerStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${space.xs};
`;

const DisclaimerTrigger = styled.button`
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  width: auto;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.small.fontSize};
  line-height: ${text.small.lineHeight};
  font-weight: 500;
  color: ${colors.primary};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

type InsuranceCheckoutLayoutProps = {
  checkout: UseInsuranceCheckoutScreenResult;
  billingDetailDrawer: ReactNode;
};

export function InsuranceCheckoutLayout({ checkout, billingDetailDrawer }: InsuranceCheckoutLayoutProps) {
  const {
    insurance,
    insuranceLoading,
    insuranceError,
    legalResult,
    insuranceName,
    insuranceShortName,
    designation,
    billingMath,
    courseInfo,
    contentCourseId,
    disclaimerText,
    consentTerms,
    reimbursementAccepted,
    setReimbursementAccepted,
    liabilityAccepted,
    setLiabilityAccepted,
    consentError,
    consentSectionRef,
    isLiabilityDrawerOpen,
    setIsLiabilityDrawerOpen,
    isSubmitting,
    forwardDisabled,
    forwardInactive,
    handleBack,
    handleForward,
    handleOpenBillingDetail,
  } = checkout;

  const bodyContent = (() => {
    if (insuranceLoading || legalResult.status === 'loading') {
      return (
        <DynamicLoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </DynamicLoadingArea>
      );
    }

    if (insuranceError) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          {insuranceError}
        </Notice>
      );
    }

    if (legalResult.status === 'error') {
      return (
        <Notice preset="warning" title="Rechtliche Informationen">
          Die rechtlichen Informationen konnten nicht geladen werden.
        </Notice>
      );
    }

    if (!insurance || !insuranceName || !billingMath) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          Bitte wähle zuerst deine Krankenkasse aus.
        </Notice>
      );
    }

    if (contentCourseId && courseInfo.status === 'loading') {
      return (
        <DynamicLoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </DynamicLoadingArea>
      );
    }

    if (courseInfo.status === 'error') {
      return (
        <Notice preset="warning" title="Kursdaten">
          {courseInfo.message}
        </Notice>
      );
    }

    const { refundRate, courseAmount, reimbursement, subsidy } = billingMath;

    const introLine =
      refundRate < 1
        ? `Die ${insuranceName} und Physiofit übernehmen die volle Kursgebühr für`
        : `Die ${insuranceName} übernimmt die volle Kursgebühr für`;

    return (
      <ContentColumn>
        <Body variant="document" tone="muted">
          {introLine} {designation ? <>deinen Kurs „{designation}“</> : null}
        </Body>
        <OnboardingCheckoutTable
          showFooter
          showPhysiofitSubsidy={refundRate < 1}
          copy={{
            a11ySummary: 'Kostenübersicht nach Kursabschluss',
            courseFeeLabel: 'Kursgebühr',
            courseFeeValue: formatEuro(courseAmount, 'plain'),
            insurerRefundLabel: `Erstattung durch die ${insuranceShortName} (${formatRefundPercent(refundRate)})`,
            insurerRefundValue: formatEuro(reimbursement, 'deduct'),
            physiofitSubsidyLabel: 'Förderung Physiofit',
            physiofitSubsidyValue: formatEuro(subsidy, 'deduct'),
            footerLabel: 'Dein Eigenanteil',
            footerValue: formatEuro(0, 'plain'),
          }}
        />
        <DisclaimerStack>
          <Body variant="document" size="small" tone="faded">
            {disclaimerText}
          </Body>
          <DisclaimerTrigger type="button" onClick={handleOpenBillingDetail}>
            So läuft die Erstattung im Detail →
          </DisclaimerTrigger>
        </DisclaimerStack>
        <Notice preset="success" icon={<SvgIcon name="shield" size={20} primaryColor={colors.success} decorative />}>
          Starte & Trainiere komplett unverbindlich. Sollte der Kurs nichts für dich sein kannst du jederzeit aufhören -
          komplett kostenlos und risikofrei.
        </Notice>
        <ConsentStack ref={consentSectionRef}>
          {consentError ? (
            <Notice preset="warning" title="Bitte bestätigen">
              Bitte bestätige die Nutzungsbedingungen um fortzufahren
            </Notice>
          ) : null}
          <Checkbox boxed checked={reimbursementAccepted} onCheckedChange={setReimbursementAccepted}>
            {consentTerms[0].checkboxText}
          </Checkbox>
          <Checkbox boxed checked={liabilityAccepted} onCheckedChange={setLiabilityAccepted}>
            {consentTerms[1].checkboxText.split('Haftungsausschluss').map((part, idx, arr) =>
              idx < arr.length - 1 ? (
                <>
                  {part}
                  <ConsentLink href="" onClick={() => setIsLiabilityDrawerOpen(true)}>
                    Haftungsausschluss
                  </ConsentLink>
                </>
              ) : (
                part
              ),
            )}
          </Checkbox>
        </ConsentStack>
      </ContentColumn>
    );
  })();

  return (
    <OnboardingLayout
      currentStep={5}
      totalSteps={6}
      forwardLabel="Jetzt starten"
      forwardDisabled={forwardDisabled}
      forwardInactive={forwardInactive}
      forwardBusy={isSubmitting}
      onBack={handleBack}
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingHeroBanner />

      <OnboardingBody>
        <div style={{ paddingBottom: space.xl }}>{bodyContent}</div>
      </OnboardingBody>
      <OnboardingFooter />
      {billingDetailDrawer}
      {legalResult.status === 'ready' ? (
        <LiabilityLegalDrawer
          open={isLiabilityDrawerOpen}
          onClose={() => setIsLiabilityDrawerOpen(false)}
          title={legalResult.legal.liabilityTitle}
          text={legalResult.legal.liabilityText}
        />
      ) : null}
    </OnboardingLayout>
  );
}
